/**
 * Markdown-to-HTML converter for AI interpretation text.
 *
 * Browser port of src/bot/utils.py:markdown_to_html().
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function markdownToHtml(text: string): string {
  // Escape HTML special characters first
  let result = escapeHtml(text);

  // Code blocks (```...```) — must be before inline code
  result = result.replace(/```(?:\w+)?\n?([\s\S]*?)```/g, '<pre>$1</pre>');

  // Inline code (`...`)
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Bold (**...**) — must be before italic
  result = result.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');

  // Italic (*...*) — careful not to match bold
  result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<i>$1</i>');

  // Headers (### ...) → <h3>
  result = result.replace(/^#{1,6}\s+(.+)$/gm, '<h3>$1</h3>');

  // Links [text](url)
  result = result.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Bullet points: lines starting with "- " → <ul><li>
  // Collect consecutive bullet lines into a single <ul>
  result = result.replace(
    /(^|\n)(- .+(?:\n- .+)*)/g,
    (_match, prefix: string, block: string) => {
      const items = block
        .split('\n')
        .map((line) => `<li>${line.replace(/^- /, '')}</li>`)
        .join('');
      return `${prefix}<ul>${items}</ul>`;
    },
  );

  // Double newlines → paragraph breaks
  result = result.replace(/\n\n+/g, '</p><p>');

  // Single newlines → <br> (but not inside <pre>, <ul>, <h3>)
  // Simple approach: replace remaining \n with <br>
  result = result.replace(/\n/g, '<br>');

  // Wrap in <p> tags
  result = `<p>${result}</p>`;

  // Clean up empty paragraphs
  result = result.replace(/<p>\s*<\/p>/g, '');

  // Fix block elements inside <p>: move <h3>, <ul>, <pre> outside <p>
  result = result.replace(/<p>(<h3>)/g, '$1');
  result = result.replace(/(<\/h3>)<\/p>/g, '$1');
  result = result.replace(/<p>(<ul>)/g, '$1');
  result = result.replace(/(<\/ul>)<\/p>/g, '$1');
  result = result.replace(/<p>(<pre>)/g, '$1');
  result = result.replace(/(<\/pre>)<\/p>/g, '$1');

  return result;
}
